# Actions

Some components just display data. Others let users make decisions that feed back into the conversation. This page covers how interactive components work.

## The action flow

1. The model calls a tool and sends structured arguments.
2. The returned data is parsed and rendered as a component.
3. The user clicks an action control on the component.
4. Your action handler runs side effects (navigation, API call, etc.).
5. The result is sent back to the assistant as a tool result.
6. The component transitions to its receipt state.

## Action types

### Display-only

The simplest pattern. A tool returns data, the component renders it. No user interaction needed.

```tsx
// Data Table, Link Preview, Item Carousel
render: ({ result }) => <DataTable {...result} />;
```

### Decision actions

The user makes a binary or multi-choice decision. The component has embedded action buttons.

```tsx
render: ({ result, addResult }) => (
  <ApprovalCard
    {...result}
    onAction={(actionId) => {
      addResult({ decision: actionId });
    }}
  />
);
```

### Control actions

The user adjusts parameters (filters, sliders, toggles). Changes are reversible until explicitly submitted.

## The shared action contract

All interactive components use the same embedded action interface:

- `actions` -- action buttons rendered by the component
- `onAction(actionId, state)` -- runs after the action and receives post-action state
- `onBeforeAction(actionId, state)` -- guard evaluated before an action runs, returning `false` cancels the action

The `state` type varies by component -- selections for option lists, values for sliders, preferences for panels.

## Forwarding results

After the user acts, the decision needs to reach the assistant. Use `addResult(...)` from your runtime to send the user's choice back as a tool result. The assistant then continues the conversation with that context.
